import { takeEvery, put, call, all, Effect, select } from "redux-saga/effects";
import {
  actionTypes,
  generateOtpSuccess,
  generateOtpError,
  verifyOtpSuccess,
  verifyOtpError,
  logoutSuccess,
  logoutError,
} from "../actions/auth";
import { authSlice } from "../reducer/auth";
import { userSlice } from "../reducer/user";
import { stateSlice } from "../reducer/state";
import { authApi as AuthApi } from "../../api/AuthApi";
import { userApi as UserApi } from "../../api/UserApi";
import { push } from "redux-first-history";
import { maybeCleanInt } from "../../utils/dataConverter";
import { PayloadAction } from "@reduxjs/toolkit";
import { RegisterPage } from "../../utils/models";
import { SignInRequestErrorModel, SignUpRequest, SignUpRequestErrorModel, SignUpRequestFields } from "../../model/auth";
import * as Sentry from '@sentry/react';
import { ApiError, SerializedApiError } from "../../model/errors/errors";
import { signUpRequestFromUser } from "../../model/util/auth";
import { User } from "../../model/user";
import { RootState } from "..";

function* signInSaga({ payload: { username, password } }: any): Generator<Effect, void, any> {
  try {
    yield put(authSlice.actions.startLoading());
    const response = yield call(AuthApi.signIn, { username, password });

    yield put(userSlice.actions.resetUser());
    yield put(authSlice.actions.signinSuccess());
    localStorage.setItem("token", response.data.access_token);
    yield put(userSlice.actions.updateUserSuccess(response.data.user));
    // Note that user redirection occurs as a side-effect in the stateSaga below
    yield put(stateSlice.actions.fetchState());
    yield put(userSlice.actions.fetchUser());
  } catch (error: SerializedApiError<SignInRequestErrorModel> | any) {
    const apiError = ApiError.deserialize<SignInRequestErrorModel>(error);
    const err = apiError?.data;
    if (err && err.errors) {
      yield put(authSlice.actions.signinError(err));
    } else {
      yield put(authSlice.actions.signinError(apiError?.toString() ?? String(error)));
    }
  }
}

function* signUpSaga(action: PayloadAction<Omit<SignUpRequest, keyof User>>): Generator<Effect, void, any> {
  const user = (yield select((state: RootState) => state.user.data!)) as User;
  const payload = {
    ...action.payload,
    username: user.username!,
    email: user.email!,
    first_name: user.first_name!,
    last_name: user.last_name!,
    address_1: user.address_1!,
    address_2: user.address_2 ?? '',
    city: user.city!,
    state: user.state!,
    zip: maybeCleanInt(user.zip)!,
    social_security: maybeCleanInt(user.social_security)!,
  } as SignUpRequest;
  
  try {
    yield put(authSlice.actions.startLoading());
    yield call(AuthApi.signUp, payload);
    yield put(authSlice.actions.signupSuccess());
    document.getElementsByClassName('grecaptcha-badge')[0].setAttribute('style', 'display: none');

    // Automatically sign user in
    yield put(authSlice.actions.signin({
      username: payload.username,
      password: payload.password
    }));
  } catch (error: SerializedApiError<SignUpRequestErrorModel> | any) {
    const apiError = ApiError.deserialize<SignUpRequestErrorModel>(error);
    const err = apiError?.data;

    // Extra monitoring added to debug registrations
    if (err && err.errors) {
      const exception = new Error(apiError.toString());
      Sentry.captureException(exception);
      yield put(authSlice.actions.signupError(err));
    } else {
      const exception = new Error(String(error));
      Sentry.captureException(exception);
      yield put(authSlice.actions.signupError(String(error)));
    }
  }
}

function* precogValidateRegistrationFieldSaga(action: PayloadAction<{request: SignUpRequest, fields: string[], next: string}>): Generator<Effect, void, any> {
  yield put(authSlice.actions.startLoading());
  try {
    yield call(AuthApi.signUpPrecogValidate, action.payload.request, action.payload.fields);
    yield put(push(action.payload.next));
    yield put(authSlice.actions.finishLoading());
  } catch (error: SerializedApiError<SignUpRequestErrorModel | SignInRequestErrorModel> | any) {
    const apiError = ApiError.deserialize<SignUpRequestErrorModel | SignInRequestErrorModel>(error);
    const err = apiError?.data;
    if (err && err.errors) {
      yield put(authSlice.actions.signinError(err));
    } else {
      yield put(authSlice.actions.signinError(String(error)));
    }
  } finally {
    yield put(authSlice.actions.finishLoading());
  }
}

function* precogAutoValidateRegistrationFieldSaga(action: PayloadAction<{fields: SignUpRequestFields, next: string, nextMode: RegisterPage}>): Generator<Effect, void, any> {
  const {fields, next} = action.payload;
  
  const request = signUpRequestFromUser(
    yield select((state) => state.user.data),
    yield select((state) => state.auth.passwordValues.password),
    yield select((state) => state.auth.passwordValues.password_confirmation),
    ''
  );

  const fieldNames = Object.keys(fields) as (keyof SignUpRequestFields)[];

  fieldNames.forEach((field) => {
    const value = fields[field];
    if (value) {
      request[field] = value;
    }
  });

  yield put(authSlice.actions.precogValidateRegistrationField({
    request,
    fields: fieldNames,
    next
  }));
}

function* generateOtpSaga({ payload: { email, phone_number, callback } }: any): Generator<Effect, void, any> {
  try {
    yield call(AuthApi.generateOtp, {
      email,
      phone_number: maybeCleanInt(phone_number),
    });

    yield put(generateOtpSuccess());
    callback();
  } catch (error: any) {
    yield put(generateOtpError(error.message));
  }
}

function* verifyOtpSaga({
  payload: { email, phone_number, phone_number_otp, callback },
}: any): Generator<Effect, void, any> {
  try {
    const response = yield call(AuthApi.verifyOtp, {
      email,
      phone_number: maybeCleanInt(phone_number),
      phone_number_otp: maybeCleanInt(phone_number_otp),
    });

    yield put(verifyOtpSuccess());
    callback();
  } catch (error) {
    yield put(verifyOtpError(error));
  }
}

function* forgotPasswordSaga({ payload: { email } }: any): Generator<Effect, void, any> {
  try {
    const response = yield call(AuthApi.forgotPassword, {
      email,
    });

    yield put(authSlice.actions.forgotPasswordRequestSuccess(response.message));
  } catch (error: any) {
    yield put(authSlice.actions.forgotPasswordRequestError(error.message));
  }
}

function* resetPasswordSaga({
  payload: { email, password, passwordConfirmation, token },
}: any): Generator<Effect, void, any> {
  try {
    yield call(AuthApi.resetPassword, {
      email,
      password,
      password_confirmation: passwordConfirmation,
      token,
    });

    yield put(authSlice.actions.makeResetPasswordRequestSuccess());
    window.location.href = "/";
  } catch (error) {
    yield put(authSlice.actions.makeResetPasswordRequestFailure(error));
  }
}

function* logoutSaga(): Generator<Effect, void, any> {
  try {
    yield put(authSlice.actions.logout);
    yield call(UserApi.logoutUser);
    yield put(logoutSuccess()); // Will reset entire Redux store
    localStorage.removeItem("token");
    localStorage.removeItem("redux");
  } catch (error: any) {
    if (error.message === 'Unauthenticated.') {
      yield put(logoutSuccess());
      localStorage.removeItem("token");
      localStorage.removeItem("redux");
      return;
    }
    yield put(logoutError(error.message));
  }
}

export default function* authSaga() {
  yield all([takeEvery(authSlice.actions.signin.type, signInSaga)]);
  yield all([takeEvery(authSlice.actions.signup.type, signUpSaga)]);
  yield all([takeEvery(authSlice.actions.precogValidateRegistrationField.type, precogValidateRegistrationFieldSaga)]);
  yield all([takeEvery(authSlice.actions.precogAutoValidateRegistrationField.type, precogAutoValidateRegistrationFieldSaga)]);
  yield all([takeEvery(actionTypes.GENERATE_OTP_SAGA, generateOtpSaga)]);
  yield all([takeEvery(actionTypes.VERIFY_OTP_SAGA, verifyOtpSaga)]);
  yield all([takeEvery(actionTypes.FORGOT_PASSWORD_SAGA, forgotPasswordSaga)]);
  yield all([takeEvery(authSlice.actions.makeResetPasswordRequest, resetPasswordSaga)]);
  yield all([takeEvery(actionTypes.LOGOUT_SAGA, logoutSaga)]);
}
